import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Snackbar,
  Checkbox,
  ListItemText,
  List,
  ListItem,
  InputAdornment,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  useMediaQuery,
  FormControlLabel,
  TableSortLabel
} from '@mui/material';
import ReminderCardView from '../../../Components/ReminderCardView/ReminderCardView';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  CalendarMonth as CalendarMonthIcon,
  Repeat as RepeatIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  getDocs,
  where
} from 'firebase/firestore';
import { DATABASE } from '../../../Config/firebase';
import FUNCTIONS_URLS from '../../../Config/functions';


const Reminders = () => {
  const [reminders, setReminders] = useState([]);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calendarView, setCalendarView] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [reminderToReschedule, setReminderToReschedule] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ date: '', time: '09:00' });
  const [editingReminder, setEditingReminder] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  
  // Detect mobile device (breakpoint at 960px)
  const isMobile = useMediaQuery("(max-width: 960px)");

  // Default categories
  const defaultCategories = [
    { value: 'client_notification', label: 'Client Notification' },
    { value: 'idea', label: 'Idea' },
    { value: 'important', label: 'Important' },
    { value: 'accounting', label: 'Accounting' }
  ];

  // Status options
  const statusOptions = [
    { value: 'pending', label: 'Pending', color: 'warning' },
    { value: 'waiting', label: 'Waiting', color: 'info' },
    { value: 'sent', label: 'Sent', color: 'success' },
    { value: 'done', label: 'Done', color: 'secondary' },
    { value: 'check_again', label: 'Check Again', color: 'warning' },
    { value: 'failed', label: 'Failed', color: 'error' }
  ];

  const STATUS_VISUALS = {
    pending: { border: '#ffc107', rowBg: 'rgba(255, 193, 7, 0.1)', selectBg: 'rgba(255, 193, 7, 0.22)' },
    waiting: { border: '#29b6f6', rowBg: 'rgba(41, 182, 246, 0.1)', selectBg: 'rgba(41, 182, 246, 0.22)' },
    sent: { border: '#00897b', rowBg: 'rgba(0, 137, 123, 0.14)', selectBg: 'rgba(0, 137, 123, 0.28)' },
    done: { border: '#7c4dff', rowBg: 'rgba(124, 77, 255, 0.12)', selectBg: 'rgba(124, 77, 255, 0.26)' },
    check_again: { border: '#ff9800', rowBg: 'rgba(255, 152, 0, 0.12)', selectBg: 'rgba(255, 152, 0, 0.22)' },
    failed: { border: '#ef5350', rowBg: 'rgba(239, 83, 80, 0.12)', selectBg: 'rgba(239, 83, 80, 0.22)' },
  };

  const getStatusVisuals = (status) => STATUS_VISUALS[status] || STATUS_VISUALS.pending;

  const [filterInputs, setFilterInputs] = useState({
    search: '',
    status: 'all',
    category: 'all',
  });
  const [activeFilters, setActiveFilters] = useState({
    search: '',
    status: 'all',
    category: 'all',
  });
  const [sortConfig, setSortConfig] = useState({
    field: 'reminder_date',
    direction: 'asc',
  });

  // Combine default and custom categories
  const allCategories = [...defaultCategories, ...customCategories];

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    reminder_date: '',
    reminder_time: '09:00',
    category: 'client_notification',
    status: 'pending',
    client_emails: [],
    client_names: [],
    custom_email: '',
    notes: '',
    advance_notifications: 0,
    sync_google_calendar: true,
  });

  const advanceEmailOptions = [
    { value: 0, label: 'On the day only' },
    { value: 1, label: '1 day before + on the day' },
    { value: 2, label: '2 and 1 days before + on the day' },
  ];

  const advanceEmailSummary = (count) => {
    const opt = advanceEmailOptions.find((o) => o.value === count);
    return opt ? opt.label : 'On the day only';
  };

  // Fetch users for client selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersQuery = query(
          collection(DATABASE, 'users'),
          where('role', '==', 1), // Only clients
          orderBy('full_name')
        );
        const snapshot = await getDocs(usersQuery);
        const usersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, []);

  // Listen to reminders
  useEffect(() => {
    const remindersQuery = query(
      collection(DATABASE, 'admin_reminders'),
      orderBy('reminder_date', 'asc')
    );

    const unsubscribe = onSnapshot(remindersQuery, (snapshot) => {
      const remindersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setReminders(remindersData);
    }, (error) => {
      console.error('Error fetching reminders:', error);
    });

    return () => unsubscribe();
  }, []);

  const handleOpenDialog = (reminder = null) => {
    if (reminder) {
      setEditingReminder(reminder);
      const date = reminder.reminder_date?.toDate();
      setFormData({
        title: reminder.title || '',
        description: reminder.description || '',
        reminder_date: date ? date.toISOString().split('T')[0] : '',
        reminder_time: reminder.reminder_time || '09:00',
        category: reminder.category || 'client_notification',
        status: reminder.status || 'pending',
        client_emails: reminder.client_emails || [],
        client_names: reminder.client_names || [],
        custom_email: reminder.custom_email || '',
        notes: reminder.notes || '',
        advance_notifications: Number(reminder.advance_notifications) || 0,
        sync_google_calendar: reminder.sync_google_calendar !== false,
      });
    } else {
      setEditingReminder(null);
      setFormData({
        title: '',
        description: '',
        reminder_date: '',
        reminder_time: '09:00',
        category: 'client_notification',
        status: 'pending',
        client_emails: [],
        client_names: [],
        custom_email: '',
        notes: '',
        advance_notifications: 0,
        sync_google_calendar: true,
      });
    }
    setUserSearch('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingReminder(null);
    setUserSearch('');
    setFormData({
      title: '',
      description: '',
      reminder_date: '',
      reminder_time: '09:00',
      category: 'client_notification',
      status: 'pending',
      client_emails: [],
      client_names: [],
      custom_email: '',
      notes: '',
      advance_notifications: 0,
      sync_google_calendar: true,
    });
  };

  const handleReschedule = (reminder) => {
    setReminderToReschedule(reminder);
    const date = reminder.reminder_date?.toDate();
    setRescheduleForm({
      date: date ? date.toISOString().split('T')[0] : '',
      time: reminder.reminder_time || '09:00'
    });
    setRescheduleDialogOpen(true);
  };

  const handleSaveReschedule = async () => {
    if (!rescheduleForm.date) {
      setSnackbar({ open: true, message: 'Date is required', severity: 'warning' });
      return;
    }

    setLoading(true);

    try {
      const url = FUNCTIONS_URLS.rescheduleReminder;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reminder_id: reminderToReschedule.id,
          new_reminder_date: rescheduleForm.date,
          new_reminder_time: rescheduleForm.time
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({ 
          open: true, 
          message: 'Reminder rescheduled successfully', 
          severity: 'success' 
        });
        setRescheduleDialogOpen(false);
        setReminderToReschedule(null);
      } else {
        throw new Error(result.error || 'Failed to reschedule reminder');
      }
    } catch (error) {
      console.error('Error rescheduling reminder:', error);
      setSnackbar({ open: true, message: 'Error rescheduling reminder', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter(user => {
    const searchLower = userSearch.toLowerCase();
    const name = (user.full_name || 'Unknown').toLowerCase();
    const email = (user.email || '').toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower);
  });

  // Select all filtered users
  const handleSelectAll = () => {
    if (formData.client_emails.length === filteredUsers.length) {
      // Deselect all
      setFormData({ ...formData, client_emails: [], client_names: [] });
    } else {
      // Select all filtered users
      const allFilteredIds = filteredUsers.map(u => u.id);
      const allFilteredEmails = filteredUsers.map(u => u.email).filter(Boolean);
      const allFilteredNames = filteredUsers.map(u => u.full_name || 'Unknown');
      setFormData({ 
        ...formData, 
        client_emails: allFilteredEmails,
        client_names: allFilteredNames
      });
    }
  };

  // Toggle individual user selection
  const handleToggleUser = (user) => {
    const currentEmails = formData.client_emails || [];
    const currentNames = formData.client_names || [];
    const userEmail = user.email;
    const userName = user.full_name || 'Unknown';

    if (currentEmails.includes(userEmail)) {
      setFormData({ 
        ...formData, 
        client_emails: currentEmails.filter(e => e !== userEmail),
        client_names: currentNames.filter((_, i) => currentEmails[i] !== userEmail)
      });
    } else {
      setFormData({ 
        ...formData, 
        client_emails: [...currentEmails, userEmail],
        client_names: [...currentNames, userName]
      });
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.reminder_date) {
      setSnackbar({ open: true, message: 'Title and date are required', severity: 'warning' });
      return;
    }

    // Validate: either client_emails or custom_email should be provided (or both can be empty for admin-only)
    if (formData.client_emails.length === 0 && !formData.custom_email) {
      // This is OK - will only send to admin@fidusfinance.com
    }

    setLoading(true);

    try {
      const url = FUNCTIONS_URLS.createAdminReminder;
      const body = {
        title: formData.title,
        description: formData.description,
        reminder_date: formData.reminder_date,
        reminder_time: formData.reminder_time,
        category: formData.category,
        status: formData.status,
        client_emails: formData.client_emails,
        client_names: formData.client_names,
        custom_email: formData.custom_email,
        notes: formData.notes,
        advance_notifications: formData.advance_notifications,
        sync_google_calendar: formData.sync_google_calendar,
        reminder_id: editingReminder?.id || null
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (result.success) {
        let message = editingReminder ? 'Reminder updated successfully' : 'Reminder created successfully';
        if (result.calendar?.error && !result.calendar?.skipped) {
          message += ` (Google Calendar: ${result.calendar.error})`;
        } else if (result.calendar?.htmlLink) {
          message += ' — synced to Google Calendar.';
        } else if (result.calendar?.skipped && result.calendar?.error) {
          message += ' (Calendar not configured yet.)';
        }
        setSnackbar({ 
          open: true, 
          message,
          severity: result.calendar?.error && !result.calendar?.skipped ? 'warning' : 'success',
        });
        handleCloseDialog();
      } else {
        throw new Error(result.error || 'Failed to save reminder');
      }
    } catch (error) {
      console.error('Error saving reminder:', error);
      setSnackbar({ open: true, message: 'Error saving reminder', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendNow = async () => {
    if (!formData.title) {
      setSnackbar({ open: true, message: 'Title is required', severity: 'warning' });
      return;
    }

    setLoading(true);

    try {
      const url = FUNCTIONS_URLS.sendReminderNow;
      const body = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        client_emails: formData.client_emails,
        client_names: formData.client_names,
        custom_email: formData.custom_email,
        notes: formData.notes
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({ 
          open: true, 
          message: 'Reminder sent immediately!', 
          severity: 'success' 
        });
        handleCloseDialog();
      } else {
        throw new Error(result.error || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      setSnackbar({ open: true, message: 'Error sending reminder', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reminderId) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) {
      return;
    }

    try {
      const response = await fetch(FUNCTIONS_URLS.deleteAdminReminder, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reminder_id: reminderId }),
      });
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Delete failed');
      }
      setSnackbar({ open: true, message: 'Reminder deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('Error deleting reminder:', error);
      setSnackbar({ open: true, message: 'Error deleting reminder', severity: 'error' });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', { timeZone: 'Europe/London' });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '—';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-GB', {
      timeZone: 'Europe/London',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusSelectSx = (status) => {
    const vis = getStatusVisuals(status || 'pending');
    return {
      minWidth: 130,
      fontWeight: 600,
      bgcolor: vis.selectBg,
      borderRadius: 1,
      '& .MuiOutlinedInput-notchedOutline': { borderColor: vis.border },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: vis.border },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: vis.border },
    };
  };

  const getReminderRowSx = (reminder) => {
    const vis = getStatusVisuals(reminder.status || 'pending');
    let bgcolor = vis.rowBg;
    if (isToday(reminder)) {
      bgcolor = 'rgba(255, 193, 7, 0.18)';
    } else if (isPastDue(reminder)) {
      bgcolor = 'rgba(239, 83, 80, 0.14)';
    }
    return {
      borderLeft: `4px solid ${vis.border}`,
      bgcolor,
      transition: 'background-color 0.2s ease',
      '&:hover': { filter: 'brightness(1.06)' },
    };
  };

  const handleApplyFilters = () => {
    setActiveFilters({ ...filterInputs });
  };

  const handleClearFilters = () => {
    const empty = { search: '', status: 'all', category: 'all' };
    setFilterInputs(empty);
    setActiveFilters(empty);
  };

  const timestampMs = (ts) => {
    if (!ts) return 0;
    return ts.toDate ? ts.toDate().getTime() : new Date(ts).getTime();
  };

  const reminderDateTimeMs = (r) => {
    if (!r.reminder_date) return 0;
    const base = r.reminder_date.toDate().getTime();
    const [h, m] = String(r.reminder_time || '09:00').split(':').map(Number);
    return base + ((h || 0) * 60 + (m || 0)) * 60 * 1000;
  };

  const clientSortKey = (r) => {
    if (r.client_emails?.length) return r.client_emails[0].toLowerCase();
    if (r.custom_email) return r.custom_email.toLowerCase();
    return '';
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const filteredReminders = useMemo(() => {
    const q = activeFilters.search.trim().toLowerCase();
    return reminders.filter((r) => {
      if (activeFilters.status !== 'all' && (r.status || 'pending') !== activeFilters.status) {
        return false;
      }
      if (activeFilters.category !== 'all' && r.category !== activeFilters.category) {
        return false;
      }
      if (!q) return true;
      const haystack = [
        r.title,
        r.description,
        r.notes,
        r.custom_email,
        ...(r.client_emails || []),
        ...(r.client_names || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [reminders, activeFilters]);

  const sortedReminders = useMemo(() => {
    const list = [...filteredReminders];
    const { field, direction } = sortConfig;
    const mult = direction === 'asc' ? 1 : -1;

    list.sort((a, b) => {
      let cmp = 0;
      switch (field) {
        case 'created_at':
          cmp = timestampMs(a.created_at) - timestampMs(b.created_at);
          break;
        case 'reminder_date':
          cmp = reminderDateTimeMs(a) - reminderDateTimeMs(b);
          break;
        case 'title':
          cmp = (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
          break;
        case 'category':
          cmp = (a.category || '').localeCompare(b.category || '', undefined, { sensitivity: 'base' });
          break;
        case 'description':
          cmp = (a.description || '').localeCompare(b.description || '', undefined, { sensitivity: 'base' });
          break;
        case 'client':
          cmp = clientSortKey(a).localeCompare(clientSortKey(b), undefined, { sensitivity: 'base' });
          break;
        case 'status':
          cmp = (a.status || 'pending').localeCompare(b.status || 'pending', undefined, { sensitivity: 'base' });
          break;
        default:
          cmp = 0;
      }
      return cmp * mult;
    });
    return list;
  }, [filteredReminders, sortConfig]);

  const sortLabel = (field, label) => (
    <TableSortLabel
      active={sortConfig.field === field}
      direction={sortConfig.field === field ? sortConfig.direction : 'asc'}
      onClick={() => handleSort(field)}
    >
      <strong>{label}</strong>
    </TableSortLabel>
  );

  const renderFiltersBar = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <FilterListIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Find reminders
        </Typography>
        <Chip
          size="small"
          label={`${sortedReminders.length} of ${reminders.length}`}
          sx={{ ml: 'auto' }}
        />
      </Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            placeholder="Title, description, client, notes…"
            value={filterInputs.search}
            onChange={(e) => setFilterInputs({ ...filterInputs, search: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleApplyFilters();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filterInputs.status}
              label="Status"
              onChange={(e) => setFilterInputs({ ...filterInputs, status: e.target.value })}
            >
              <MenuItem value="all">All statuses</MenuItem>
              {statusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={filterInputs.category}
              label="Category"
              onChange={(e) => setFilterInputs({ ...filterInputs, category: e.target.value })}
            >
              <MenuItem value="all">All categories</MenuItem>
              {allCategories.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleApplyFilters}
              sx={{
                flex: 1,
                minWidth: 120,
                background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
                '&:hover': { opacity: 0.9, background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)' },
              }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleClearFilters} sx={{ flex: 1, minWidth: 90 }}>
              Clear
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  const isPastDue = (reminder) => {
    if (!reminder.reminder_date) return false;
    const reminderDate = reminder.reminder_date.toDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return reminderDate < today && !reminder.sent;
  };

  const isToday = (reminder) => {
    if (!reminder.reminder_date) return false;
    const reminderDate = reminder.reminder_date.toDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const reminderDateOnly = new Date(reminderDate);
    reminderDateOnly.setHours(0, 0, 0, 0);
    return reminderDateOnly.getTime() === today.getTime();
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'client_notification': return 'primary';
      case 'idea': return 'info';
      case 'important': return 'error';
      case 'accounting': return 'warning';
      default: 
        // For custom categories, use a default color
        return 'default';
    }
  };

  const getCategoryLabel = (category) => {
    const cat = allCategories.find(c => c.value === category);
    return cat ? cat.label : category || 'General';
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(s => s.value === status);
    return statusOption ? statusOption.color : 'default';
  };

  const getStatusLabel = (status) => {
    const statusOption = statusOptions.find(s => s.value === status);
    return statusOption ? statusOption.label : status || 'Pending';
  };

  const handlePreviousMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  // Calendar view functions
  const getRemindersForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return reminders.filter(r => {
      if (!r.reminder_date) return false;
      const reminderDate = r.reminder_date.toDate();
      const reminderDateStr = reminderDate.toISOString().split('T')[0];
      return reminderDateStr === dateStr;
    });
  };

  const renderCalendar = () => {
    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const weeks = [];
    let currentWeek = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      currentWeek.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(calendarYear, calendarMonth, day);
      currentWeek.push(date);

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    // Add remaining empty cells
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handlePreviousMonth} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6">
            {new Date(calendarYear, calendarMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </Typography>
          <IconButton onClick={handleNextMonth} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <Box>
          {/* Day headers */}
          <Grid container spacing={0.5} sx={{ mb: 0.5 }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <Grid item xs={12/7} key={day} sx={{ textAlign: 'center', fontWeight: 'bold', py: 1 }}>
                {day}
              </Grid>
            ))}
          </Grid>
          {/* Weeks */}
          {weeks.map((week, weekIdx) => (
            <Grid container spacing={0.5} key={weekIdx} sx={{ mb: 0.5 }}>
              {week.map((date, dayIdx) => {
                if (!date) {
                  return (
                    <Grid 
                      item 
                      xs={12/7} 
                      key={`${weekIdx}-${dayIdx}`} 
                      sx={{ 
                        minHeight: 100,
                        border: '1px solid #e0e0e0',
                        bgcolor: '#f5f5f5'
                      }} 
                    />
                  );
                }
                const dayReminders = getRemindersForDate(date);
                const isTodayDate = date.toDateString() === new Date().toDateString();
                
                return (
                  <Grid 
                    item 
                    xs={12/7}
                    key={`${weekIdx}-${dayIdx}`}
                    sx={{
                      minHeight: 100,
                      border: '1px solid #e0e0e0',
                      p: 0.5,
                      bgcolor: isTodayDate ? 'rgba(255, 193, 7, 0.1)' : 'white',
                      cursor: dayReminders.length > 0 ? 'pointer' : 'default',
                      '&:hover': dayReminders.length > 0 ? { bgcolor: 'action.hover' } : {},
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onClick={() => {
                      if (dayReminders.length > 0) {
                        // Show reminders for this day
                        const firstReminder = dayReminders[0];
                        handleOpenDialog(firstReminder);
                      }
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: isTodayDate ? 'bold' : 'normal',
                        mb: 0.5
                      }}
                    >
                      {date.getDate()}
                    </Typography>
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                      {dayReminders.slice(0, 2).map((reminder, idx) => (
                        <Chip
                          key={reminder.id}
                          label={reminder.title.length > 12 ? reminder.title.substring(0, 12) + '...' : reminder.title}
                          size="small"
                          color={getCategoryColor(reminder.category)}
                          sx={{ 
                            fontSize: '0.6rem', 
                            height: 16, 
                            mb: 0.25,
                            display: 'block',
                            width: '100%',
                            '& .MuiChip-label': { px: 0.5, py: 0 }
                          }}
                        />
                      ))}
                      {dayReminders.length > 2 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          +{dayReminders.length - 2}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ))}
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 },
      paddingTop: { xs: '80px', md: '24px' }, // Espaço para evitar sobreposição em mobile
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' }, 
        mb: 3,
        gap: { xs: 2, md: 0 }
      }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '24px', md: '32px' } }}>
          Scheduled Reminders
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            variant={calendarView ? 'contained' : 'outlined'}
            startIcon={<CalendarMonthIcon />}
            onClick={() => setCalendarView(!calendarView)}
            sx={{
              ...(calendarView ? {
                background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
                '&:hover': {
                  background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
                  opacity: 0.9
                }
              } : {})
            }}
          >
            Calendar View
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
              '&:hover': {
                background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
                opacity: 0.9
              }
            }}
          >
            Create Reminder
          </Button>
        </Box>
      </Box>

      {calendarView ? (
        renderCalendar()
      ) : (
        <>
          {renderFiltersBar()}
          {/* Desktop: Table (comportamento atual) */}
          {!isMobile && (
            <Paper sx={{ width: '100%', mb: 2 }}>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={sortConfig.field === 'created_at' ? sortConfig.direction : false}>
                        {sortLabel('created_at', 'Created date')}
                      </TableCell>
                      <TableCell sortDirection={sortConfig.field === 'reminder_date' ? sortConfig.direction : false}>
                        {sortLabel('reminder_date', 'Reminder details')}
                      </TableCell>
                      <TableCell sortDirection={sortConfig.field === 'title' ? sortConfig.direction : false}>
                        {sortLabel('title', 'Title')}
                      </TableCell>
                      <TableCell sortDirection={sortConfig.field === 'category' ? sortConfig.direction : false}>
                        {sortLabel('category', 'Category')}
                      </TableCell>
                      <TableCell sortDirection={sortConfig.field === 'description' ? sortConfig.direction : false}>
                        {sortLabel('description', 'Description')}
                      </TableCell>
                      <TableCell sortDirection={sortConfig.field === 'client' ? sortConfig.direction : false}>
                        {sortLabel('client', 'Client')}
                      </TableCell>
                      <TableCell sortDirection={sortConfig.field === 'status' ? sortConfig.direction : false}>
                        {sortLabel('status', 'Status')}
                      </TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedReminders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography color="text.secondary">
                            {reminders.length === 0 ? 'No reminders found' : 'No reminders match your filters'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedReminders.map((reminder) => (
                        <TableRow 
                          key={reminder.id} 
                          hover
                          sx={getReminderRowSx(reminder)}
                        >
                          <TableCell>
                            <Typography variant="body2">{formatDateTime(reminder.created_at)}</Typography>
                            {reminder.updated_at && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Updated {formatDateTime(reminder.updated_at)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarIcon fontSize="small" />
                              <Box>
                                <Typography variant="body2">{formatDate(reminder.reminder_date)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {reminder.reminder_time || '09:00'} (UK)
                                </Typography>
                                {reminder.google_calendar_html_link && (
                                  <Typography variant="caption" component="a" href={reminder.google_calendar_html_link} target="_blank" rel="noopener noreferrer" sx={{ display: 'block' }}>
                                    Google Calendar
                                  </Typography>
                                )}
                                {reminder.google_calendar_sync_error && (
                                  <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                    Calendar sync failed
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{reminder.title}</TableCell>
                          <TableCell>
                            <Chip 
                              label={getCategoryLabel(reminder.category)} 
                              size="small" 
                              color={getCategoryColor(reminder.category)}
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Typography variant="body2" noWrap>
                              {reminder.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {reminder.client_emails && reminder.client_emails.length > 0 ? (
                              <>
                                <Typography variant="body2">{reminder.client_emails.length} client(s)</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {reminder.client_emails.slice(0, 2).join(', ')}
                                  {reminder.client_emails.length > 2 ? ` +${reminder.client_emails.length - 2} more` : ''}
                                </Typography>
                              </>
                            ) : reminder.custom_email ? (
                              <Typography variant="body2">{reminder.custom_email}</Typography>
                            ) : (
                              'Admin only'
                            )}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={reminder.status || 'pending'}
                              onChange={async (e) => {
                                try {
                                  await updateDoc(doc(DATABASE, 'admin_reminders', reminder.id), {
                                    status: e.target.value,
                                    updated_at: new Date()
                                  });
                                } catch (error) {
                                  console.error('Error updating status:', error);
                                  setSnackbar({ open: true, message: 'Error updating status', severity: 'error' });
                                }
                              }}
                              size="small"
                              sx={getStatusSelectSx(reminder.status || 'pending')}
                            >
                              {statusOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenDialog(reminder)}
                                title="Edit reminder"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleReschedule(reminder)}
                                title="Remind me again"
                              >
                                <RepeatIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(reminder.id)}
                                title="Delete reminder"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
          
          {/* Mobile: Cards (nova visualização) */}
          {isMobile && (
            <ReminderCardView
              reminders={sortedReminders}
              loading={loading}
              formatDateTime={formatDateTime}
              getStatusVisuals={getStatusVisuals}
              getStatusSelectSx={getStatusSelectSx}
              onEdit={handleOpenDialog}
              onDelete={handleDelete}
              onReschedule={handleReschedule}
              onStatusChange={async (reminderId, newStatus) => {
                try {
                  await updateDoc(doc(DATABASE, 'admin_reminders', reminderId), {
                    status: newStatus,
                    updated_at: new Date()
                  });
                } catch (error) {
                  console.error('Error updating status:', error);
                  setSnackbar({ open: true, message: 'Error updating status', severity: 'error' });
                }
              }}
              getCategoryLabel={getCategoryLabel}
              getCategoryColor={getCategoryColor}
              getStatusLabel={getStatusLabel}
              getStatusColor={getStatusColor}
              statusOptions={statusOptions}
              isToday={isToday}
              isPastDue={isPastDue}
              formatDate={formatDate}
            />
          )}
        </>
      )}

      {/* Create/Edit Reminder Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editingReminder ? 'Edit Reminder' : 'Create Reminder'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title *"
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Category"
                >
                  {allCategories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCategoryDialogOpen(true)}
                sx={{ mt: 1, minWidth: 100 }}
              >
                Add Category
              </Button>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                label="Status"
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Reminder Date *"
                type="date"
                fullWidth
                value={formData.reminder_date}
                onChange={(e) => setFormData({ ...formData, reminder_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Time *"
                type="time"
                fullWidth
                value={formData.reminder_time}
                onChange={(e) => setFormData({ ...formData, reminder_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Advance email notices</InputLabel>
              <Select
                value={formData.advance_notifications}
                onChange={(e) => setFormData({ ...formData, advance_notifications: Number(e.target.value) })}
                label="Advance email notices"
              >
                {advanceEmailOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.sync_google_calendar}
                  onChange={(e) => setFormData({ ...formData, sync_google_calendar: e.target.checked })}
                />
              }
              label="Add to Google Calendar (admin@fidusfinance.com)"
            />
            
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Select Clients (optional)
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search clients by name or email..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
              <Box sx={{ 
                border: '1px solid #ccc', 
                borderRadius: 1, 
                maxHeight: 200, 
                overflow: 'auto',
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, pb: 1, borderBottom: '1px solid #eee' }}>
                  <Checkbox
                    checked={filteredUsers.length > 0 && formData.client_emails.length === filteredUsers.length}
                    indeterminate={formData.client_emails.length > 0 && formData.client_emails.length < filteredUsers.length}
                    onChange={handleSelectAll}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Select All ({filteredUsers.length} client{filteredUsers.length !== 1 ? 's' : ''})
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                    {formData.client_emails.length} selected
                  </Typography>
                </Box>
                <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                  {filteredUsers.length === 0 ? (
                    <ListItem>
                      <Typography variant="body2" color="text.secondary">
                        No clients found
                      </Typography>
                    </ListItem>
                  ) : (
                    filteredUsers.map((user) => (
                      <ListItem 
                        key={user.id} 
                        button
                        onClick={() => handleToggleUser(user)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <Checkbox 
                          checked={formData.client_emails.includes(user.email)}
                          onChange={() => handleToggleUser(user)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <ListItemText 
                          primary={user.full_name || 'Unknown'} 
                          secondary={user.email}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </Box>
            </Box>

            <Divider />

            <TextField
              label="Custom Email (if no client selected)"
              fullWidth
              type="email"
              value={formData.custom_email}
              onChange={(e) => setFormData({ ...formData, custom_email: e.target.value })}
              helperText="Enter a custom email address if reminder is not for a registered client"
              disabled={formData.client_emails.length > 0}
            />

            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes or context..."
            />
            <Alert severity="info">
              <strong>Fiddux emails</strong> (order@fiddux.com → admin@fidusfinance.com
              {formData.client_emails.length > 0 ? ` + ${formData.client_emails.length} client(s)` : formData.custom_email ? ` + ${formData.custom_email}` : ''}):
              {advanceEmailSummary(formData.advance_notifications)} at {formData.reminder_time || '09:00'} Europe/London
              {formData.reminder_date ? ` (${new Date(formData.reminder_date).toLocaleDateString()})` : ''}. Checked hourly by the server; saving also tries to send if already due.
              {formData.sync_google_calendar ? (
                <>
                  {' '}
                  <strong>Google Calendar</strong> gets the event plus native email/popup alerts (1 day before when selected) — separate from Fiddux emails.
                </>
              ) : null}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {!editingReminder && (
            <Button
              onClick={handleSendNow}
              variant="outlined"
              color="success"
              disabled={loading || !formData.title}
              sx={{ mr: 1 }}
            >
              {loading ? 'Sending...' : 'Send Now'}
            </Button>
          )}
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{
              background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
            }}
          >
            {loading ? 'Saving...' : editingReminder ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Reminder Dialog */}
      <Dialog 
        open={rescheduleDialogOpen} 
        onClose={() => {
          setRescheduleDialogOpen(false);
          setReminderToReschedule(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Remind Me Again</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Alert severity="info">
              This will create a new reminder based on "{reminderToReschedule?.title}" with the new date and time.
            </Alert>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="New Date *"
                type="date"
                fullWidth
                value={rescheduleForm.date}
                onChange={(e) => setRescheduleForm({ ...rescheduleForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="New Time *"
                type="time"
                fullWidth
                value={rescheduleForm.time}
                onChange={(e) => setRescheduleForm({ ...rescheduleForm, time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRescheduleDialogOpen(false);
            setReminderToReschedule(null);
          }}>Cancel</Button>
          <Button
            onClick={handleSaveReschedule}
            variant="contained"
            disabled={loading}
            sx={{
              background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
            }}
          >
            {loading ? 'Rescheduling...' : 'Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog 
        open={categoryDialogOpen} 
        onClose={() => {
          setCategoryDialogOpen(false);
          setNewCategoryName('');
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Add Custom Category</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Category Name *"
              fullWidth
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="e.g., Marketing, Legal, Support"
            />
            <Alert severity="info">
              Custom categories will be available for all reminders.
            </Alert>
            {customCategories.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Existing Custom Categories:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {customCategories.map((cat, idx) => (
                    <Chip
                      key={idx}
                      label={cat.label}
                      onDelete={() => {
                        setCustomCategories(customCategories.filter((_, i) => i !== idx));
                      }}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCategoryDialogOpen(false);
            setNewCategoryName('');
          }}>Cancel</Button>
          <Button
            onClick={() => {
              if (newCategoryName.trim()) {
                const newCategory = {
                  value: newCategoryName.toLowerCase().replace(/\s+/g, '_'),
                  label: newCategoryName.trim()
                };
                // Check if already exists
                if (!allCategories.find(c => c.value === newCategory.value)) {
                  setCustomCategories([...customCategories, newCategory]);
                  setNewCategoryName('');
                  setSnackbar({ open: true, message: 'Category added successfully', severity: 'success' });
                } else {
                  setSnackbar({ open: true, message: 'Category already exists', severity: 'warning' });
                }
              }
            }}
            variant="contained"
            disabled={!newCategoryName.trim()}
            sx={{
              background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
            }}
          >
            Add Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reminders;
